const propertyInputType: Record<StyleType, StyleValueType> = {
  gradient: 'color_picker_rgba',
  font_color: 'color_picker_rgba',
  stroke: 'color_picker_rgba',
  aspect_ratio: 'number_input',
  border_radius: 'number_input',
  corner_radius: 'number_input',
  border_thickness: 'number_input',
  border_color: 'color_picker_rgba',
  border_style: 'select',
  grid_columns: 'number_input',
  opacity: 'number_input',
  background_color: 'color_picker_rgba',
  background_image: 'text_input',
  gap: 'number_input',
  gutter: 'number_input',
  width: 'number_input',
  height: 'number_input',
  underline_thickness: 'number_input',
  divider_thickness: 'number_input',
  font_size: 'number_input',
  font_weight: 'number_input',
  line_clamp: 'number_input',
  number_of_lines: 'number_input',
  object_position: 'text_input',
  alignment: 'select',
  padding_top: 'number_input',
  padding_right: 'number_input',
  padding_bottom: 'number_input',
  padding_left: 'number_input',
  font_family: 'web_font_selector',
  margin_top: 'number_input',
  margin_right: 'number_input',
  margin_bottom: 'number_input',
  margin_left: 'number_input',
  line_height: 'number_input',
  letter_spacing: 'number_input',
  caret_color: 'color_picker_rgba',
  image_shadow: 'text_input',
  text_shadow: 'text_input',
  shadow: 'text_input',
  background_blur: 'number_input',
  max_width: 'number_input',
  y_position: 'number_input',
  plugin_configuration_id: 'cell_style_selector',
  select: 'select',
  switch: 'switch',
};

// #region Overload Signatures
export function createStyleField<
  P extends string,
  T extends ColorStringFields
>(config: {
  key: P;
  type: T;
  label: string;
  initial_value?: ColorString;
  conditional_fields?: ConditionalFields;
}): Field<P, T>;

export function createStyleField<
  P extends string,
  T extends NumberFields
>(config: {
  key: P;
  type: T;
  label: string;
  initial_value?: number;
  conditional_fields?: ConditionalFields;
}): Field<P, T>;

export function createStyleField<
  P extends string,
  T extends StringFields
>(config: {
  key: P;
  type: T;
  label: string;
  initial_value?: string;
  conditional_fields?: ConditionalFields;
}): Field<P, T>;

export function createStyleField<
  P extends string,
  T extends BooleanFields
>(config: {
  key: P;
  type: T;
  label: string;
  initial_value?: boolean;
  conditional_fields?: ConditionalFields;
}): Field<P, T>;
// #endregion

/**
 * The implementation function for the createStyleField to ensures correct
 * types from the overload signatures.
 * @param key - The prefix for the field
 * @param type - Type from StyleType
 * @param label - The label for the field
 * @param initial_value - (Optional) The initial value for the field
 * @param conditional_fields - (Optional) The conditional fields
 * @returns
 */
export function createStyleField<P extends string, T extends StyleType>({
  key,
  type,
  label,
  initial_value,
  conditional_fields,
}: {
  key: P;
  type: T;
  label: string;
  initial_value?: InitialValue;
  conditional_fields?: ConditionalFields;
}): Field<P, T> {
  return {
    key: `${key}_${type}`,
    label,
    initial_value,
    type: propertyInputType[type],
    conditional_fields,
  } as const;
}

// #region Types
type Field<P extends string, T extends StyleType> = {
  readonly key: `${P}_${T}`;
  readonly label: string;
  readonly initial_value?: InitialValue;
  readonly type: (typeof propertyInputType)[T];
  readonly conditional_fields?: ConditionalFields;
};

type ResponsiveField<
  P extends string,
  T extends StyleType,
  R extends ResponsiveBreakpoint
> = Omit<Field<P, T>, 'key'> & {
  readonly key: `${P}_${T}_${R}`;
};

type ColorString =
  | `rgba(${number},${number},${number},${number})`
  | 'transparent';

type InitialValue = string | number | boolean;

type ConditionalFields = {
  key: string;
  condition_value: InitialValue;
}[];

type ResponsiveConditionalFields<
  P extends string,
  R extends ResponsiveBreakpoint
> = {
  key: `${P}_${R}`;
  condition_value: InitialValue;
}[];

type Options = {
  readonly text: string;
  readonly value: string;
}[];

type StyleType =
  | 'gradient'
  | 'font_color'
  | 'stroke'
  | 'aspect_ratio'
  | 'border_radius'
  | 'corner_radius'
  | 'border_thickness'
  | 'border_color'
  | 'border_style'
  | 'grid_columns'
  | 'opacity'
  | 'background_color'
  | 'background_image'
  | 'gap'
  | 'gutter'
  | 'width'
  | 'height'
  | 'underline_thickness'
  | 'divider_thickness'
  | 'font_size'
  | 'font_weight'
  | 'line_clamp'
  | 'number_of_lines'
  | 'object_position'
  | 'alignment'
  | 'padding_top'
  | 'padding_right'
  | 'padding_bottom'
  | 'padding_left'
  | 'font_family'
  | 'margin_top'
  | 'margin_right'
  | 'margin_bottom'
  | 'margin_left'
  | 'line_height'
  | 'letter_spacing'
  | 'caret_color'
  | 'image_shadow'
  | 'text_shadow'
  | 'shadow'
  | 'background_blur'
  | 'max_width'
  | 'y_position'
  | 'plugin_configuration_id'
  | 'select'
  | 'switch';

type StyleValueType =
  | 'text_input'
  | 'number_input'
  | 'color_picker_rgba'
  | 'web_font_selector'
  | 'cell_style_selector'
  | 'select'
  | 'switch';

type ColorStringFields = Extract<
  StyleType,
  | 'gradient'
  | 'font_color'
  | 'stroke'
  | 'border_color'
  | 'background_color'
  | 'caret_color'
>;

type NumberFields = Extract<
  StyleType,
  | 'aspect_ratio'
  | 'border_radius'
  | 'corner_radius'
  | 'border_thickness'
  | 'grid_columns'
  | 'opacity'
  | 'gap'
  | 'gutter'
  | 'width'
  | 'height'
  | 'underline_thickness'
  | 'divider_thickness'
  | 'font_size'
  | 'font_weight'
  | 'line_clamp'
  | 'number_of_lines'
  | 'padding_top'
  | 'padding_right'
  | 'padding_bottom'
  | 'padding_left'
  | 'margin_top'
  | 'margin_right'
  | 'margin_bottom'
  | 'margin_left'
  | 'line_height'
  | 'letter_spacing'
  | 'background_blur'
  | 'max_width'
  | 'y_position'
>;

type StringFields = Extract<
  StyleType,
  | 'background_image'
  | 'object_position'
  | 'alignment'
  | 'image_shadow'
  | 'text_shadow'
  | 'shadow'
  | 'border_style'
  | 'plugin_configuration_id'
  | 'select'
  | 'font_family'
>;

type BooleanFields = Extract<StyleType, 'switch'>;

// #endregion

// #region bulk creation of responsive style fields

type ResponsiveBreakpoint = 'mobile' | 'tablet' | 'desktop' | 'large_desktop';

const responsiveBreakpoints: ResponsiveBreakpoint[] = [
  'mobile',
  'tablet',
  'desktop',
  'large_desktop',
];

function getResponsiveConditionalFields(
  breakpoint: ResponsiveBreakpoint,
  conditional_fields?: ConditionalFields,
  responsive_conditional_fields?: boolean
): {
  conditional_fields?:
    | ConditionalFields
    | ResponsiveConditionalFields<string, ResponsiveBreakpoint>;
} {
  return conditional_fields
    ? {
        conditional_fields: responsive_conditional_fields
          ? conditional_fields.map(({ key, ...field }) => ({
              key: `${key}_${breakpoint}`,
              ...field,
            }))
          : conditional_fields,
      }
    : {};
}

// #region Overload Signatures
export function createResponsiveStyleFields<
  P extends string,
  T extends ColorStringFields,
  R extends ResponsiveBreakpoint
>(config: {
  key: P;
  type: T;
  label: string;
  initial_value?: ColorString;
  conditional_fields?: ConditionalFields;
  responsive_values?: Record<ResponsiveBreakpoint, ColorString>;
  responsive_conditional_fields?: boolean;
  options?: Options;
}): ResponsiveField<P, T, R>[];

export function createResponsiveStyleFields<
  P extends string,
  T extends NumberFields,
  R extends ResponsiveBreakpoint
>(config: {
  key: P;
  type: T;
  label: string;
  initial_value?: number;
  conditional_fields?: ConditionalFields;
  responsive_values?: Record<ResponsiveBreakpoint, number>;
  responsive_conditional_fields?: boolean;
  options?: Options;
}): ResponsiveField<P, T, R>[];

export function createResponsiveStyleFields<
  P extends string,
  T extends StringFields,
  R extends ResponsiveBreakpoint
>(config: {
  key: P;
  type: T;
  label: string;
  initial_value?: string;
  conditional_fields?: ConditionalFields;
  responsive_values?: Record<ResponsiveBreakpoint, string>;
  responsive_conditional_fields?: boolean;
  options?: Options;
}): ResponsiveField<P, T, R>[];

export function createResponsiveStyleFields<
  P extends string,
  T extends BooleanFields,
  R extends ResponsiveBreakpoint
>(config: {
  key: P;
  type: T;
  label: string;
  initial_value?: boolean;
  conditional_fields?: ConditionalFields;
  responsive_values?: Record<ResponsiveBreakpoint, boolean>;
  responsive_conditional_fields?: boolean;
  options?: Options;
}): ResponsiveField<P, T, R>[];
// #endregion

/**
 * The implementation function for the createResponsiveStyleFields to ensures correct
 * types from the overload signatures.
 * @param key - The prefix for the field
 * @param type - Type from StyleType
 * @param label - The label for the field
 * @param initial_value - (Optional) The initial value for the field
 * @param conditional_fields - (Optional) The conditional fields
 * @param responsive_values - (Optional) The responsive initial values for the field
 * @param responsive_conditional_fields - (Optional) Whether to make the conditional fields responsive. Defaults to true.
 * @param options - (Optional) List of options for a select field
 * @returns
 */
export function createResponsiveStyleFields<
  P extends string,
  T extends StyleType,
  R extends ResponsiveBreakpoint
>({
  key,
  type,
  label,
  initial_value,
  conditional_fields,
  responsive_values,
  responsive_conditional_fields = true,
  options,
}: {
  key: P;
  type: T;
  label: string;
  initial_value?: InitialValue;
  conditional_fields?: ConditionalFields;
  responsive_values?: Record<ResponsiveBreakpoint, InitialValue>;
  responsive_conditional_fields?: boolean;
  options?: Options;
}): ResponsiveField<P, T, R>[] {
  return responsiveBreakpoints.map((breakpoint) => ({
    key: `${key}_${type}_${breakpoint}` as `${P}_${T}_${R}`,
    label: `${label} ${breakpoint}`,
    type: propertyInputType[type],
    initial_value: responsive_values?.[breakpoint] ?? initial_value,
    ...getResponsiveConditionalFields(
      breakpoint,
      conditional_fields,
      responsive_conditional_fields
    ),
    options,
  }));
}

// #endregion

// #region bulk creation of margin and padding fields

type Margin = 'margin_top' | 'margin_right' | 'margin_bottom' | 'margin_left';

const margin: Margin[] = [
  'margin_top',
  'margin_right',
  'margin_bottom',
  'margin_left',
];

export function createMarginFields<P extends string>({
  key,
  label,
  initial_values,
  initial_value,
  conditional_fields,
}: {
  key: P;
  label: string;
  conditional_fields?: ConditionalFields;
} & (
  | { initial_values: Record<Margin, number>; initial_value?: never }
  | { initial_value: number; initial_values?: never }
)): Field<P, Margin>[] {
  return margin.map((type) =>
    createStyleField({
      key,
      type: type as Extract<StyleType, Margin>,
      label: `${label} ${type.replaceAll('_', ' ')}`,
      initial_value: initial_value ?? initial_values?.[type],
      conditional_fields,
    })
  );
}

export function createResponsiveMarginFields<P extends string>({
  key,
  label,
  initial_values,
  initial_value,
  conditional_fields,
  responsive_values,
  responsive_conditional_fields,
}: {
  key: P;
  label: string;
  conditional_fields?: ConditionalFields;
  responsive_conditional_fields?: boolean;
} & (
  | {
      responsive_values: Record<Margin, Record<ResponsiveBreakpoint, number>>;
      initial_value?: never;
      initial_values?: never;
    }
  | {
      initial_values: Record<Margin, number>;
      initial_value?: never;
      responsive_values?: never;
    }
  | { initial_value: number; initial_values?: never; responsive_values?: never }
)): ResponsiveField<P, Margin, ResponsiveBreakpoint>[] {
  return margin.flatMap((type) =>
    createResponsiveStyleFields({
      key,
      type,
      label: `${label} ${type.replaceAll('_', ' ')}`,
      initial_value: initial_value ?? initial_values?.[type],
      conditional_fields,
      responsive_values: responsive_values?.[type],
      responsive_conditional_fields,
    })
  );
}

type Padding =
  | 'padding_top'
  | 'padding_right'
  | 'padding_bottom'
  | 'padding_left';

const padding: Padding[] = [
  'padding_top',
  'padding_right',
  'padding_bottom',
  'padding_left',
];

export function createPaddingFields<P extends string>({
  key,
  label,
  initial_values,
  initial_value,
  conditional_fields,
}: {
  key: P;
  label: string;
  conditional_fields?: ConditionalFields;
} & (
  | { initial_values: Record<Padding, number>; initial_value?: never }
  | { initial_value: number; initial_values?: never }
)): Field<P, Padding>[] {
  return padding.map((type) =>
    createStyleField({
      key,
      type: type as Extract<StyleType, Padding>,
      label: `${label} ${type.replaceAll('_', ' ')}`,
      initial_value: initial_value ?? initial_values?.[type],
      conditional_fields,
    })
  );
}

export function createResponsivePaddingFields<P extends string>({
  key,
  label,
  initial_values,
  initial_value,
  conditional_fields,
  responsive_values,
  responsive_conditional_fields,
}: {
  key: P;
  label: string;
  conditional_fields?: ConditionalFields;
  responsive_conditional_fields?: boolean;
} & (
  | {
      responsive_values: Record<Padding, Record<ResponsiveBreakpoint, number>>;
      initial_value?: never;
      initial_values?: never;
    }
  | {
      initial_values: Record<Padding, number>;
      initial_value?: never;
      responsive_values?: never;
    }
  | { initial_value: number; initial_values?: never; responsive_values?: never }
)): ResponsiveField<P, Padding, ResponsiveBreakpoint>[] {
  return padding.flatMap((type) =>
    createResponsiveStyleFields({
      key,
      type,
      label: `${label} ${type.replaceAll('_', ' ')}`,
      initial_value: initial_value ?? initial_values?.[type],
      conditional_fields,
      responsive_values: responsive_values?.[type],
      responsive_conditional_fields,
    })
  );
}

// #endregion

// #region bulk creation of underline fields

type Underline =
  | 'underline_thickness'
  | 'underline_corner_radius'
  | 'underline_background_color'
  | 'underline_background_color_hover'
  | 'underline_background_color_selected'
  | 'underline_background_color_selected_hover';

const underline: Underline[] = [
  'underline_thickness',
  'underline_corner_radius',
  'underline_background_color',
  'underline_background_color_hover',
  'underline_background_color_selected',
  'underline_background_color_selected_hover',
];

const underlinePropertyInputType: Record<
  Underline,
  'number_input' | 'color_picker_rgba'
> = {
  underline_thickness: 'number_input',
  underline_corner_radius: 'number_input',
  underline_background_color: 'color_picker_rgba',
  underline_background_color_hover: 'color_picker_rgba',
  underline_background_color_selected: 'color_picker_rgba',
  underline_background_color_selected_hover: 'color_picker_rgba',
};

type UnderlineInitialValues = {
  underline_thickness: number;
  underline_corner_radius: number;
  underline_background_color: ColorString;
  underline_background_color_hover: ColorString;
  underline_background_color_selected?: ColorString;
  underline_background_color_selected_hover?: ColorString;
};

type UnderlineResponsiveValues = {
  underline_thickness: Record<ResponsiveBreakpoint, number>;
  underline_corner_radius: Record<ResponsiveBreakpoint, number>;
  underline_background_color: Record<ResponsiveBreakpoint, ColorString>;
  underline_background_color_hover: Record<ResponsiveBreakpoint, ColorString>;
  underline_background_color_selected?: Record<
    ResponsiveBreakpoint,
    ColorString
  >;
  underline_background_color_selected_hover?: Record<
    ResponsiveBreakpoint,
    ColorString
  >;
};

type UnderlineField<P extends string, T extends Underline> = {
  readonly key: `${P}_${T}`;
  readonly label: string;
  readonly initial_value?: UnderlineInitialValues[T];
  readonly type: (typeof underlinePropertyInputType)[T];
  readonly conditional_fields?: ConditionalFields;
};

type UnderlineResponsiveField<
  P extends string,
  T extends Underline,
  R extends ResponsiveBreakpoint
> = Omit<UnderlineField<P, T>, 'key'> & {
  readonly key: `${P}_${T}_${R}`;
};

export function createUnderlineFields<P extends string>({
  key,
  label,
  initial_values,
  conditional_fields,
}: {
  key: P;
  label: string;
  conditional_fields?: ConditionalFields;
  initial_values: UnderlineInitialValues;
}): UnderlineField<P, Underline>[] {
  return underline.map((type) => {
    return {
      key: `${key}_${type}` as `${P}_${Underline}`,
      type: underlinePropertyInputType[type],
      label: `${label} ${type.replaceAll('_', ' ')}`,
      initial_value: initial_values?.[type],
      conditional_fields,
    } as const;
  });
}

export function createResponsiveUnderlineFields<P extends string>({
  key,
  label,
  initial_values,
  conditional_fields,
  responsive_values,
  responsive_conditional_fields = true,
}: {
  key: P;
  label: string;
  conditional_fields?: ConditionalFields;
  initial_values: UnderlineInitialValues;
  responsive_values?: UnderlineResponsiveValues;
  responsive_conditional_fields?: boolean;
}): UnderlineResponsiveField<P, Underline, ResponsiveBreakpoint>[] {
  return underline.flatMap((type) =>
    responsiveBreakpoints.map((variant) => {
      return {
        key: `${key}_${type}_${variant}` as const,
        type: underlinePropertyInputType[type],
        label: `${label} ${type.replaceAll('_', ' ')} ${variant}`,
        initial_value:
          responsive_values?.[type]?.[variant] ?? initial_values?.[type],
        ...getResponsiveConditionalFields(
          variant,
          conditional_fields,
          responsive_conditional_fields
        ),
      };
    })
  );
}

// #endregion

// #region bulk creation of font fields

type Font = 'font_family' | 'font_size' | 'line_height' | 'letter_spacing';

const font: Font[] = [
  'font_family',
  'font_size',
  'line_height',
  'letter_spacing',
];

const fontPropertyInputType: Record<
  Font,
  'web_font_selector' | 'number_input'
> = {
  font_family: 'web_font_selector',
  font_size: 'number_input',
  line_height: 'number_input',
  letter_spacing: 'number_input',
};

type FontField<P extends string, T extends Font> = {
  readonly key: `${P}_${T}`;
  readonly label: string;
  readonly initial_value?: FontInitialValues[T];
  readonly type: (typeof fontPropertyInputType)[T];
  readonly conditional_fields?: ConditionalFields;
};

type FontInitialValues = {
  font_family: string;
  font_size: number;
  line_height: number;
  letter_spacing: number;
};

type FontResponsiveValues = {
  font_family: Record<ResponsiveBreakpoint, string>;
  font_size: Record<ResponsiveBreakpoint, number>;
  line_height: Record<ResponsiveBreakpoint, number>;
  letter_spacing: Record<ResponsiveBreakpoint, number>;
};

type FontResponsiveField<
  P extends string,
  T extends Font,
  R extends ResponsiveBreakpoint
> = Omit<FontField<P, T>, 'key'> & {
  readonly key: `${P}_${T}_${R}`;
};

export function createFontFields<P extends string>({
  key,
  label,
  initial_values,
  conditional_fields,
}: {
  key: P;
  label: string;
  conditional_fields?: ConditionalFields;
  initial_values: FontInitialValues;
}): FontField<P, Font>[] {
  return font.map((type) => {
    return {
      key: `${key}_${type}` as `${P}_${Font}`,
      type: fontPropertyInputType[type],
      label: `${label} ${type.replaceAll('_', ' ')}`,
      initial_value: initial_values?.[type],
      conditional_fields,
    } as const;
  });
}

export function createResponsiveFontFields<P extends string>({
  key,
  label,
  conditional_fields,
  initial_values,
  responsive_values,
  responsive_conditional_fields = true,
}: {
  key: P;
  label: string;
  conditional_fields?: ConditionalFields;
  initial_values?: FontInitialValues;
  responsive_values?: FontResponsiveValues;
  responsive_conditional_fields?: boolean;
}): FontResponsiveField<P, Font, ResponsiveBreakpoint>[] {
  return font.flatMap((type) =>
    responsiveBreakpoints.map((variant) => {
      return {
        key: `${key}_${type}_${variant}` as const,
        type: fontPropertyInputType[type],
        label: `${label} ${type.replaceAll('_', ' ')} ${variant}`,
        initial_value:
          responsive_values?.[type]?.[variant] ?? initial_values?.[type],
        ...getResponsiveConditionalFields(
          variant,
          conditional_fields,
          responsive_conditional_fields
        ),
      };
    })
  );
}

// #endregion

// #region bulk creation of shadow fields

type Shadow =
  | 'shadow_x_offset'
  | 'shadow_y_offset'
  | 'shadow_blur'
  | 'shadow_spread'
  | 'shadow_color';

const shadow: Shadow[] = [
  'shadow_x_offset',
  'shadow_y_offset',
  'shadow_blur',
  'shadow_spread',
  'shadow_color',
];

const shadowPropertyInputType: Record<
  Shadow,
  'number_input' | 'color_picker_rgba'
> = {
  shadow_x_offset: 'number_input',
  shadow_y_offset: 'number_input',
  shadow_blur: 'number_input',
  shadow_spread: 'number_input',
  shadow_color: 'color_picker_rgba',
};

type ShadowInitialValues = {
  shadow_x_offset: number;
  shadow_y_offset: number;
  shadow_blur: number;
  shadow_spread: number;
  shadow_color: ColorString;
};

type ShadowResponsiveValues = {
  shadow_x_offset: Record<ResponsiveBreakpoint, number>;
  shadow_y_offset: Record<ResponsiveBreakpoint, number>;
  shadow_blur: Record<ResponsiveBreakpoint, number>;
  shadow_spread: Record<ResponsiveBreakpoint, number>;
  shadow_color: Record<ResponsiveBreakpoint, ColorString>;
};

type ShadowField<P extends string, T extends Shadow> = {
  readonly key: `${P}_${T}`;
  readonly label: string;
  readonly initial_value?: ShadowInitialValues[T];
  readonly type: (typeof shadowPropertyInputType)[T];
  readonly conditional_fields?: ConditionalFields;
};

type ShadowResponsiveField<
  P extends string,
  T extends Shadow,
  R extends ResponsiveBreakpoint
> = Omit<ShadowField<P, T>, 'key'> & {
  readonly key: `${P}_${T}_${R}`;
};

export function createShadowFields<P extends string>({
  key,
  label,
  initial_values,
  conditional_fields,
}: {
  key: P;
  label: string;
  conditional_fields?: ConditionalFields;
  initial_values: ShadowInitialValues;
}): ShadowField<P, Shadow>[] {
  return shadow.map((type) => {
    const label: string = type.replaceAll('_', ' ').replace(/^shadow\s*/, '');

    return {
      key: `${key}_${type}` as `${P}_${Shadow}`,
      type: shadowPropertyInputType[type],
      label: `${label.charAt(0).toUpperCase()}${label.slice(1)}`,
      initial_value: initial_values?.[type],
      conditional_fields,
    } as const;
  });
}

export function createResponsiveShadowFields<P extends string>({
  key,
  conditional_fields,
  initial_values,
  responsive_values,
  responsive_conditional_fields = true,
}: {
  key: P;
  conditional_fields?: ConditionalFields;
  initial_values?: ShadowInitialValues;
  responsive_values?: ShadowResponsiveValues;
  responsive_conditional_fields?: boolean;
}): ShadowResponsiveField<P, Shadow, ResponsiveBreakpoint>[] {
  return shadow.flatMap((type) =>
    responsiveBreakpoints.map((variant) => {
      const label: string = type.replaceAll('_', ' ').replace(/^shadow\s*/, '');

      return {
        key: `${key}_${type}_${variant}` as const,
        label: `${label.charAt(0).toUpperCase()}${label.slice(1)} ${variant}`,
        type: shadowPropertyInputType[type],
        initial_value:
          responsive_values?.[type]?.[variant] ?? initial_values?.[type],
        ...getResponsiveConditionalFields(
          variant,
          conditional_fields,
          responsive_conditional_fields
        ),
      };
    })
  );
}

// #endregion

// #region bulk creation of component state fields

type StyleState = 'default' | 'hover' | 'selected' | 'selected_hover';

const states: StyleState[] = ['default', 'hover', 'selected', 'selected_hover'];

const labels: Record<StyleState, string> = {
  default: 'Default',
  hover: 'Hover',
  selected: 'Selected',
  selected_hover: 'Selected & hover',
};

type StyleStateField<
  P extends string,
  S extends StyleState,
  T extends StyleType
> = Omit<Field<P, T>, 'key'> & {
  readonly key: `${P}_${S}_${T}`;
};

export function createStyleStateFields<P extends string, T extends StyleType>({
  key,
  label,
  type,
  initial_value,
  initial_values,
  conditional_fields,
}: {
  key: P;
  label: string;
  type: T;
  conditional_fields?: ConditionalFields;
} & (
  | {
      initial_values: Record<StyleState, string | number>;
      initial_value?: never;
    }
  | { initial_value: string | number; initial_values?: never }
)): StyleStateField<P, StyleState, T>[] {
  return states.map((state) => {
    return {
      key: `${key}_${state}_${type}` as `${P}_${StyleState}_${T}`,
      label: `${labels[state]} ${label}`,
      initial_value: initial_values?.[state] ?? initial_value,
      type: propertyInputType[type],
      conditional_fields,
    } as const;
  });
}

// #endregion
